import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '../ui/Tooltip';
import { MoveCard } from './MoveCard';
import { abbreviateLocationName, formatMinutes } from './utils';
import { MoveRisk, MoveGroup, ProjectedStatus } from './types';
import styles from './CompactMoveCard.module.css';
import { RouteEta } from './RouteEta';
import type { RouteCascadeEstimate } from './routeEtaCascade';

export interface CompactMoveCardProps {
  move: MoveRisk;
  routeCascade?: RouteCascadeEstimate;
  projectedStatus?: ProjectedStatus;
  projectedETA?: string;
  isETAToDestination?: boolean;
  onProClick?: (pronumber: number, legnum: number) => void;
  group?: MoveGroup;
  onDragStart?: (e: React.DragEvent, move: MoveRisk, sourceDriver: string, group?: MoveGroup) => void;
  onDragEnd?: (e: React.DragEvent) => void;
  showChassis?: boolean;
}

// Map status to single letter
const getStatusLetter = (status: string): string => {
  switch (status) {
    case 'Dispatched': return 'D';
    case 'Started': return 'S';
    case 'In Transit': return 'T';
    case 'At Origin': return 'O';
    case 'At Destination': return 'A';
    case 'Completed': return 'C';
    default: return '?';
  }
};

// Map status to full display name
const getStatusDisplayName = (status: string): string => {
  switch (status) {
    case 'Dispatched': return 'Not Started';
    case 'Started': return 'Started';
    case 'In Transit': return 'In Transit';
    case 'At Origin': return 'At Origin';
    case 'At Destination': return 'At Destination';
    case 'Completed': return 'Completed';
    default: return status;
  }
};

// Get CSS class for status badge
const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'Dispatched': return styles.statusBadgeDispatched;
    case 'Started': return styles.statusBadgeStarted;
    case 'In Transit': return styles.statusBadgeInTransit;
    case 'At Origin': return styles.statusBadgeAtOrigin;
    case 'At Destination': return styles.statusBadgeAtDestination;
    case 'Completed': return styles.statusBadgeCompleted;
    default: return styles.statusBadgeDispatched;
  }
};

// Get CSS class for risk border
const getRiskBorderClass = (projectedStatus?: ProjectedStatus): string => {
  switch (projectedStatus) {
    case 'at_risk': return styles.compactCardAtRisk;
    case 'will_miss': return styles.compactCardWillMiss;
    case 'missed': return styles.compactCardMissed;
    default: return styles.compactCardOnTrack;
  }
};

// Get CSS class for ETA time color
const getETATimeClass = (projectedStatus?: ProjectedStatus): string => {
  switch (projectedStatus) {
    case 'at_risk': return styles.etaTimeAtRisk;
    case 'will_miss':
    case 'missed': return styles.etaTimeLate;
    default: return styles.etaTimeOnTrack;
  }
};

// Check if a date string (M/D) is today
const isDateToday = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return false;
  const today = new Date();
  const [month, day] = dateStr.split('/').map(Number);
  return today.getMonth() + 1 === month && today.getDate() === day;
};

// For exports, return the earlier of Cutoff or Per Diem
const getExportSubDate = (move: { movetype: string; per_diem_date: string | null; cutoff_date: string | null; cutoff_time?: string | null }): { label: string; date: string; time?: string } | null => {
  const isExport = move.movetype !== 'IMPORT';
  const pd = move.per_diem_date;
  const co = isExport ? move.cutoff_date : null;
  const coTime = isExport ? (move.cutoff_time ?? undefined) : undefined;

  if (!pd && !co) return null;
  if (!co) return pd ? { label: 'Per Diem', date: pd } : null;
  if (!pd) return isExport ? { label: 'Cutoff', date: co, time: coTime } : null;

  const year = new Date().getFullYear();
  const [pdM, pdD] = pd.split('/').map(Number);
  const [coM, coD] = co.split('/').map(Number);
  const pdDate = new Date(year, pdM - 1, pdD);
  const coDate = new Date(year, coM - 1, coD);

  return coDate <= pdDate ? { label: 'Cutoff', date: co, time: coTime } : { label: 'Per Diem', date: pd };
};

export const CompactMoveCard: React.FC<CompactMoveCardProps> = ({
  move,
  routeCascade,
  projectedStatus,
  projectedETA,
  isETAToDestination,
  onProClick,
  group,
  onDragStart,
  onDragEnd,
  showChassis,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Calculate popover position
  useEffect(() => {
    if (showPopover && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const popoverWidth = 370; // MoveCard width + padding
      const popoverHeight = 220; // Approximate height

      let top = rect.bottom + 8;
      let left = rect.left - popoverWidth + rect.width;

      // Keep within viewport
      if (left < 8) left = 8;
      if (left + popoverWidth > window.innerWidth - 8) {
        left = window.innerWidth - popoverWidth - 8;
      }
      if (top + popoverHeight > window.innerHeight - 8) {
        top = rect.top - popoverHeight - 8;
      }

      setPopoverPosition({ top, left });
    }
  }, [showPopover]);

  // Close popover when clicking outside
  useEffect(() => {
    if (!showPopover) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPopover]);

  const isCompleted = move.status === 'Completed';
  const isNotStarted = move.status === 'Dispatched';
  const isDraggable = isNotStarted && !!onDragStart;

  // Dimmed when it doesn't pass the active dispatcher/region filter. Dimming
  // lives in CSS (not an inline style) so the .filteredOut:hover rule can
  // temporarily restore the card to full opacity for clicking the Pro link.
  const isFilteredOut = move.matches_dispatcher_filter === false || move.matches_region_filter === false;

  // Any delivery reschedule (service failure) on the pro gets a red card border.
  const isRescheduled = (move.reschedule_count ?? 0) > 0;

  return (
    <>
      <div
        className={`${styles.compactCard} ${getRiskBorderClass(projectedStatus)} ${isRescheduled ? styles.compactCardRescheduled : ''} ${isFilteredOut ? styles.filteredOut : ''}`}
        draggable={isDraggable}
        onDragStart={isDraggable ? (e) => onDragStart!(e, move, move.driver, group) : undefined}
        onDragEnd={onDragEnd}
      >
        {/* Status Badge */}
        <Tooltip content={getStatusDisplayName(move.status)} placement="right">
          <div className={`${styles.statusBadge} ${getStatusBadgeClass(move.status)}`}>
            {getStatusLetter(move.status)}
          </div>
        </Tooltip>
        {(() => {
          const tickets = move.trouble_tickets || [];
          const openTickets = tickets.filter(t => t.status.toUpperCase() === 'NEW' || t.status.toUpperCase() === 'PENDING');
          if (openTickets.length === 0) return null;
          return (
            <Tooltip
              content={
                <div className="text-xs space-y-2 max-w-xs">
                  <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
                    Trouble Tickets ({move.trouble_ticket_count})
                  </div>
                  {tickets.map((ticket) => (
                    <div key={ticket.recno} className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-gray-900 font-medium">{ticket.category || 'No Category'}</span>
                        <span className={`inline-flex px-1 py-0.5 rounded text-[0.625rem] font-medium ${
                          ticket.status.toUpperCase() === 'NEW' || ticket.status.toUpperCase() === 'PENDING'
                            ? 'bg-danger text-danger-fg'
                            : ticket.status.toUpperCase() === 'RESOLVED'
                              ? 'bg-ok text-ok-fg'
                              : 'bg-gray-100 text-gray-800'
                        }`}>
                          {ticket.status}
                        </span>
                      </div>
                      <div className="text-gray-400 text-[0.625rem]">{ticket.datetime}</div>
                    </div>
                  ))}
                </div>
              }
              placement="bottom"
            >
              <span className="inline-flex items-center justify-center w-4 h-4 text-[0.5rem] font-bold text-white bg-red-500 rounded-full cursor-help flex-shrink-0">
                {openTickets.length}
              </span>
            </Tooltip>
          );
        })()}

        {/* Main Content */}
        <div className={styles.cardContent}>
          {/* Row 1: Badges, PRO, Equipment */}
          <div className={styles.row1}>
            {/* PRO Link */}
            <button
              className={styles.proLink}
              onClick={(e) => {
                e.stopPropagation();
                onProClick?.(move.pronumber, move.legnum);
              }}
            >
              {move.pronumber}-{move.legnum}
            </button>

            {/* Trouble Ticket Badge */}
            {move.trouble_ticket_count != null && move.trouble_ticket_count > 0 && (() => {
              const tickets = move.trouble_tickets || [];
              const hasActive = tickets.some(
                (t) => t.status.toUpperCase() === 'NEW' || t.status.toUpperCase() === 'PENDING'
              );
              return (
                <Tooltip
                  content={
                    <div className="text-xs space-y-2 max-w-xs">
                      <div className="font-semibold border-b border-gray-300 pb-1 mb-1 text-gray-900">
                        Driver Trouble Tickets ({move.trouble_ticket_count})
                      </div>
                      {tickets.map((ticket) => (
                        <div key={ticket.recno} className="space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-gray-900 font-medium">{ticket.category || 'No Category'}</span>
                            <span className={`inline-flex px-1 py-0.5 rounded text-[0.625rem] font-medium ${
                              ticket.status.toUpperCase() === 'NEW' || ticket.status.toUpperCase() === 'PENDING'
                                ? 'bg-danger text-danger-fg'
                                : ticket.status.toUpperCase() === 'RESOLVED'
                                  ? 'bg-ok text-ok-fg'
                                  : 'bg-gray-100 text-gray-800'
                            }`}>
                              {ticket.status}
                            </span>
                          </div>
                          <div className="text-gray-400 text-[0.625rem]">{ticket.datetime}</div>
                        </div>
                      ))}
                    </div>
                  }
                  placement="bottom"
                >
                  <span
                    className={`inline-flex items-center justify-center px-1 py-0.5 text-[0.5625rem] font-bold text-white rounded cursor-help whitespace-nowrap ${
                      hasActive ? 'bg-red-500' : 'bg-green-500'
                    }`}
                  >
                    TT: {move.trouble_ticket_count}
                  </span>
                </Tooltip>
              );
            })()}

            <div className={styles.badgeGroup}>
              {/* Import/Export badge */}
              <Tooltip content={move.movetype === 'IMPORT' ? 'Import' : 'Export'} placement="bottom">
                <div className={`${styles.moveTypeBadgeAbbrev} ${move.movetype === 'IMPORT' ? styles.moveTypeBadgeImport : styles.moveTypeBadgeExport}`}>
                  {move.movetype === 'IMPORT' ? 'I' : 'E'}
                </div>
              </Tooltip>
              {/* Live/Drop badge */}
              <Tooltip content={move.legdrop === 'Y' ? 'Drop' : 'Live'} placement="bottom">
                <div className={`${styles.liveDropBadgeAbbrev} ${move.legdrop === 'Y' ? styles.dropBadge : styles.liveBadge}`}>
                  {move.legdrop === 'Y' ? 'D' : 'L'}
                </div>
              </Tooltip>
            </div>
            {/* Equipment column: container + size on top, chassis below */}
            <div className={styles.equipmentColumn}>
              {(move.container || move.size) && (
                <div className={styles.equipmentRow}>
                  {move.container && (
                    <span className={styles.equipmentNumber}>{move.container}</span>
                  )}
                  {move.size && (
                    <span className={styles.equipmentNumber}>{move.size}</span>
                  )}
                </div>
              )}
              {showChassis && move.chassisid && (
                <Tooltip content={`Chassis${move.chassistype ? ` (${move.chassistype})` : ''}: ${move.chassisid}`} placement="bottom">
                  <span className={styles.equipmentNumber}>{move.chassisid}</span>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Row 2: Route Names + Deadline */}
          <div className={styles.row2}>
            <div className={styles.routeName}>
              {abbreviateLocationName(move.origin_name)} → {abbreviateLocationName(move.dest_name)}
            </div>
            <div className={styles.timingInfo}>
              {move.deadline_time && (
                <span className={styles.apptTime}>{move.deadline_time}</span>
              )}
              {(() => {
                const subDate = getExportSubDate(move);
                return subDate ? (
                  <span className={isDateToday(subDate.date) ? styles.perDiemBadge : styles.perDiemBadgeGrey}>
                    {subDate.label} {subDate.date}{subDate.time ? ` ${subDate.time}` : ''}
                  </span>
                ) : null;
              })()}
              {move.legtype === 'PICKUP' && (
                move.storage_cleared_through ? (
                  <span className={isDateToday(move.storage_cleared_through) ? styles.perDiemBadge : styles.perDiemBadgeGrey}>
                    Stor Clr: {move.storage_cleared_through}
                  </span>
                ) : move.lfd ? (
                  <span className={isDateToday(move.lfd) ? styles.perDiemBadge : styles.perDiemBadgeGrey}>
                    LFD: {move.lfd}
                  </span>
                ) : null
              )}
            </div>
          </div>

          {/* Row 3: City/State + ETA */}
          {!isCompleted && <RouteEta moveStatus={move.status} data={move.route_eta} cascade={routeCascade} />}
          <div className={styles.row3}>
            <div className={styles.routeCityState}>
              {move.origin_city_state} → {move.dest_city_state} - ({formatMinutes(move.route_duration_minutes)})
            </div>
            {/* ETA Display */}
            {!isCompleted && projectedETA && (
              <div className={styles.etaDisplay}>
                <span className={styles.etaLabel}>ETA:</span>
                <span className={`${styles.etaTime} ${getETATimeClass(projectedStatus)}`}>
                  {projectedETA}
                </span>
                <span className={styles.etaDestLabel}>
                  {isETAToDestination ? 'to Dest' : 'to Orig'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Details Button */}
        <button
          ref={buttonRef}
          className={styles.detailsButton}
          onClick={() => setShowPopover(!showPopover)}
          title="View full details"
        >
          <MagnifyingGlassIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Full MoveCard Popover */}
      {showPopover && createPortal(
        <>
          <div className={styles.popoverOverlay} onClick={() => setShowPopover(false)} />
          <div
            ref={popoverRef}
            className={styles.popoverContainer}
            style={{ top: popoverPosition.top, left: popoverPosition.left }}
          >
            <MoveCard
              move={move}
              routeCascade={routeCascade}
              priority={0}
              isCurrentMove={false}
              projectedStatus={projectedStatus}
              projectedETA={projectedETA}
              isETAToDestination={isETAToDestination}
              onProClick={onProClick}
            />
          </div>
        </>,
        document.body
      )}
    </>
  );
};

export default CompactMoveCard;
